import { plural } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import React from "react";

import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner/InfoBanner";
import { IconProps } from "@whoosh/design-system/src/components/primitive";

import { RestrictionFeature } from "../../types";
import { RestrictionInfoBarIcon } from "./components/RestrictionInfoBarIcon/RestrictionInfoBarIcon";
import { getMessage } from "./utils/getMessage/getMessage";
import { getRestrictionVariation } from "./utils/getRestrictionVariation/getRestrictionVariation";
import { shouldShowBar } from "./utils/shouldShowBar/shouldShowBar";

type RestrictionInfoBarProps = {
  restriction: RestrictionFeature.RestrictionBarData;
  restrictionContext: "Staff" | "Patron";
};

export const RestrictionInfoBar = ({
  restriction,
  restrictionContext,
}: RestrictionInfoBarProps) => {
  useLingui();
  const shouldShow = shouldShowBar(restriction, restrictionContext);
  const message = getMessage(
    restriction,
    restrictionContext,
    (count, countLimit) =>
      plural(count, {
        one: `# of ${countLimit} Used`,
        other: `# of ${countLimit} Used`,
      })
  );
  const variation = getRestrictionVariation(restriction, restrictionContext);

  const colorTheme =
    variation === "error"
      ? "ERROR"
      : variation === "warning"
      ? "WARNING"
      : "BLUE";

  const Icon = React.useCallback(
    (props: IconProps) => (
      <RestrictionInfoBarIcon variation={variation} {...props} />
    ),
    [variation]
  );

  return shouldShow ? (
    <InfoBanner
      leadingIcon={Icon}
      message={message ?? ""}
      colorTheme={colorTheme}
      testID={`restriction-info-bar-${message
        ?.toLowerCase()
        .replace(/ /g, "-")}`}
    />
  ) : null;
};
